import { shiftSprint } from '@nedelnik/shared';

import { periodNeedsDates, periodSubtitle, periodTitle } from '../sprint/labels.js';

import type { SprintKey, Task } from '@nedelnik/shared';

/**
 * Меню спринта: ровно шесть пунктов (3.5.6).
 *
 * Одно и то же меню открывается из двух мест — из поля «Спринт» в карточке и
 * значком календаря у задачи во Входящих. Раньше это были два разных списка с
 * разными подписями («через спринт» против «Неделя 38»), и человек видел
 * разные ответы на один и тот же вопрос «куда переложить». Разметка здесь
 * общая, разное только то, откуда меню открыли.
 *
 * Устройство подчинено одному правилу: самый частый следующий шаг — первой
 * кнопкой во всю ширину, дальше периоды подряд, а «убрать из спринта» —
 * последним и красным.
 *
 * Шесть — не круглое число: больше не помещается на экране телефона, меньше не
 * покрывает обычного планирования. Поэтому список считается от того, сколько
 * кнопок заняло место, а не задан отдельной константой.
 */

/** Сколько пунктов в меню всего: кнопки плюс периоды (3.5.6). */
const MENU_ITEMS = 6;

/**
 * Самый частый следующий шаг для текущего места задачи.
 *
 * Из Входящих и из любого чужого периода — «в текущий»; из текущего — «в
 * следующий». Отдельной кнопки «в следующий» у задачи из Входящих нет: следующий
 * период и так первый в списке, а кнопка занимала бы место, ничего не добавляя.
 */
function forwardAction(task: Task, current: SprintKey): { label: string; sprint: SprintKey } {
  return task.sprint === current
    ? { label: 'в следующий', sprint: shiftSprint(current, 1) }
    : { label: 'в текущий', sprint: current };
}

/**
 * Периоды списка: подряд от текущего, кроме занятых кнопкой и кроме того, где
 * задача уже стоит.
 *
 * «Перенести туда, где и так стоишь» — строка, которая ничего не делает;
 * текущее положение написано на самом поле выше. Прошлые периоды не
 * предлагаются вовсе: планировать в них нечего.
 */
function sprintList(task: Task, current: SprintKey, taken: number): SprintKey[] {
  const forward = forwardAction(task, current);
  const skip = new Set<SprintKey | null>([forward.sprint, task.sprint]);

  const list: SprintKey[] = [];
  const room = MENU_ITEMS - taken;
  for (let offset = 0; list.length < room && offset < room + skip.size + 2; offset += 1) {
    const key = shiftSprint(current, offset);
    if (!skip.has(key)) list.push(key);
  }

  return list;
}

export function SprintMenu({
  task,
  current,
  label,
  onPick,
}: {
  task: Task;
  /** Период, от которого считается «текущий»: показанный на экране. */
  current: SprintKey;
  /** Имя меню для озвучки: у поля в карточке и у значка в строке оно разное. */
  label: string;
  onPick: (sprint: SprintKey | null) => void;
}): React.ReactElement {
  const forward = forwardAction(task, current);
  // «Убрать из спринта» есть только у задачи со спринтом: из Входящих вынимать
  // неоткуда. Оно же занимает пункт, поэтому список считает и его.
  const removable = task.sprint !== null;
  const list = sprintList(task, current, removable ? 2 : 1);

  return (
    <span className="menu__list" role="menu" aria-label={label}>
      {/*
        Частый шаг — первым и во всю ширину: из Входящих и из чужого периода
        это «в текущий», из текущего — «в следующий». Период назван прямо на
        кнопке, иначе «в следующий» заставляет считать в уме.
      */}
      <button
        type="button"
        role="menuitem"
        className="quickbtn menu__forward"
        onClick={() => onPick(forward.sprint)}
      >
        {forward.label} ({periodTitle(forward.sprint)})
      </button>

      {list.map((key) => (
        <button
          key={key}
          type="button"
          role="menuitem"
          className="menu__item"
          onClick={() => onPick(key)}
        >
          {periodTitle(key)}
          {periodNeedsDates(key) && <span className="menu__dates">{periodSubtitle(key)}</span>}
        </button>
      ))}

      {/*
        «Убрать из спринта» — последним и красным: это единственный шаг,
        который не переносит, а вынимает. Среди переносов он выглядел бы таким
        же, как они, а он другой.
      */}
      {removable && (
        <button
          type="button"
          role="menuitem"
          className="quickbtn menu__remove"
          onClick={() => onPick(null)}
        >
          убрать из спринта
        </button>
      )}
    </span>
  );
}
